import { usePage } from "@inertiajs/react";
import { Monitor, Smartphone } from "lucide-react";

import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { GROUPS } from "@/constants/groups";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { canSeeApprovalComponent } from "@/lib/authz";
import type { SharedData } from "@/types";

import PurchaseRequestShowLayoutFull, {
  type PurchaseRequestShowProps,
} from "./components/purchase-request-show-layout-full";
import PurchaseRequestShowLayoutSimple from "./components/purchase-request-show-layout-simple";
import { usePurchaseRequestDecisionItems } from "./hooks/use-purchase-request-decision-items";
import { usePurchaseRequestViewMode } from "./hooks/use-purchase-request-view-mode";

export default function PurchaseRequestShow(props: PurchaseRequestShowProps) {
  const shared = usePage<SharedData>().props;
  const routes = useOrganizationRoutes();
  const { viewMode, setViewMode } = usePurchaseRequestViewMode();
  const userGroups = shared.auth?.groups ?? [];
  const CAN_MANAGE_QUOTES = [GROUPS.buyer, GROUPS.supervisor, GROUPS.admin, GROUPS.owner];

  const canSeeDecisionData = canSeeApprovalComponent(
    userGroups,
    props.approval,
    shared.auth?.user?.id ?? 0,
    CAN_MANAGE_QUOTES,
  );

  const decisionModel = usePurchaseRequestDecisionItems(
    props.price_comparison,
    props.last_purchases_by_request_item_id,
  );

  const purchaseRequestsHref = routes.purchaseRequests.index();
  const purchaseRequestHref = routes.purchaseRequests.show(props.purchase_request.id);
  const commentsHref = routes.purchaseRequests.comments(props.purchase_request.id);

  const handleViewModeChange = (nextValue: string) => {
    if (nextValue !== "simple" && nextValue !== "full") return;
    setViewMode(nextValue);
  };

  const viewModeToggle = canSeeDecisionData ? (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={handleViewModeChange}
      variant="outline"
      size="sm"
      className="w-full sm:w-auto"
      aria-label="Cambiar vista"
    >
      <ToggleGroupItem
        value="simple"
        className="gap-1"
        title="Vista simple para decisión rápida"
      >
        <Smartphone className="h-3.5 w-3.5" />
        Simple
      </ToggleGroupItem>
      <ToggleGroupItem
        value="full"
        className="gap-1"
        title="Vista completa"
      >
        <Monitor className="h-3.5 w-3.5" />
        Completa
      </ToggleGroupItem>
    </ToggleGroup>
  ) : null;

  return canSeeDecisionData && viewMode === "simple" ? (
    <PurchaseRequestShowLayoutSimple
      purchaseRequest={props.purchase_request}
      approval={props.approval}
      decisionItems={decisionModel.items}
      selectedSuppliers={decisionModel.selectedSuppliers}
      comments={props.comments}
      commentsHref={commentsHref}
      statusHistory={props.status_history ?? []}
      purchaseRequestsHref={purchaseRequestsHref}
      purchaseRequestHref={purchaseRequestHref}
      viewModeToggle={viewModeToggle}
    />
  ) : (
    <PurchaseRequestShowLayoutFull {...props} viewModeToggle={viewModeToggle} />
  );
}
